/**
 * useDisplaySettings Hook — T146P2
 *
 * Manages global display density and per-page column visibility with:
 * - Redux state (immediate local updates)
 * - Debounced server sync via PUT /auth/profile
 * - Graceful fallback on sync failure (keeps local state)
 */

import { useCallback, useEffect, useRef, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import {
  setItemDisplayDensity,
  setItemColumnVisibility,
  initializeFromUserPreferences,
  type ColumnVisibility,
} from '@/features/displaySettings/displaySettingsSlice';
import { useToast } from '@/hooks/use-toast';
import api, { endpoints } from '@/services/api';

const SYNC_DEBOUNCE_MS = 1000;

export function useDisplaySettings() {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const displaySettings = useAppSelector((state) => state.displaySettings);
  const user = useAppSelector((state) => state.auth.user);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSyncingRef = useRef(false);

  // Initialize from user preferences on app start
  useEffect(() => {
    if (user?.preferences) {
      dispatch(
        initializeFromUserPreferences({
          itemDisplayDensity: user.preferences.itemDisplayDensity,
          itemColumnsByPage: user.preferences.itemColumnsByPage,
          itemColumns: user.preferences.itemColumns,
        })
      );
    }
  }, [dispatch, user?.preferences]);

  const syncPreferences = useCallback(
    async (
      itemDisplayDensity?: 'compact' | 'receipt' | 'detailed',
      itemColumnsByPage?: Record<string, ColumnVisibility>
    ) => {
      if (isSyncingRef.current) return;
      isSyncingRef.current = true;

      try {
        // Convert ColumnVisibility objects to generic Record<string, boolean> for API
        const apiPayload: Record<string, unknown> = {};
        if (itemDisplayDensity) {
          apiPayload.itemDisplayDensity = itemDisplayDensity;
        }
        if (itemColumnsByPage) {
          apiPayload.itemColumnsByPage = itemColumnsByPage;
        }

        await api.put(endpoints.auth.profile, {
          preferences: apiPayload,
        });
      } catch {
        toast({
          title: 'Failed to save display preferences',
          description: 'Using local settings. Changes will sync when connection returns.',
          variant: 'destructive',
        });
      } finally {
        isSyncingRef.current = false;
      }
    },
    [toast]
  );

  const updateDensity = useCallback(
    (density: 'compact' | 'receipt' | 'detailed') => {
      dispatch(setItemDisplayDensity(density));

      // Debounce server sync
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = setTimeout(
        () => syncPreferences(density),
        SYNC_DEBOUNCE_MS
      );
    },
    [dispatch, syncPreferences]
  );

  const updateColumnVisibility = useCallback(
    (page: string | undefined, visibility: ColumnVisibility) => {
      dispatch(setItemColumnVisibility({ page, visibility }));

      // Persist immediately to localStorage so AC17 tests can read without server round-trip
      if (page) {
        try {
          localStorage.setItem(`ft-item-columns-${page}`, JSON.stringify(visibility));
        } catch {
          // silently ignore quota / private-mode errors
        }
      }

      // Determine what to sync based on context
      let syncColumnsByPage: Record<string, ColumnVisibility> | undefined;
      if (page) {
        // Per-page: build the full itemColumnsByPage object
        syncColumnsByPage = {
          ...displaySettings.itemColumnsByPage,
          [page]: visibility,
        };
      }

      // Debounce server sync
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = setTimeout(
        () => syncPreferences(undefined, syncColumnsByPage),
        SYNC_DEBOUNCE_MS
      );
    },
    [dispatch, displaySettings.itemColumnsByPage, syncPreferences]
  );

  return {
    itemDisplayDensity: displaySettings.itemDisplayDensity,
    itemColumnsByPage: displaySettings.itemColumnsByPage,
    itemColumns: displaySettings.itemColumns,
    updateDensity,
    updateColumnVisibility,
  };
}

export function useItemColumnVisibility(page: 'transactions' | 'detail' | 'form' | 'review') {
  const { itemColumnsByPage, updateColumnVisibility } = useDisplaySettings();

  const visibility = useMemo(
    () => itemColumnsByPage[page] || {
      emoji: true,
      qty: true,
      category: true,
      notes: false,
    },
    [itemColumnsByPage, page]
  );

  const toggleColumn = useCallback(
    (column: keyof ColumnVisibility) => {
      const newVisibility = { ...visibility, [column]: !visibility[column] };
      updateColumnVisibility(page, newVisibility);
    },
    [page, visibility, updateColumnVisibility]
  );

  const resetColumns = useCallback(() => {
    const defaults: ColumnVisibility = {
      emoji: true,
      qty: true,
      category: true,
      notes: false,
    };
    updateColumnVisibility(page, defaults);
  }, [page, updateColumnVisibility]);

  return {
    visibility,
    toggleColumn,
    resetColumns,
  };
}
